<script setup>
import { ref, onMounted } from 'vue'
import ProductCard from '../components/ProductCard.vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const featuredProducts = ref([])
const loading = ref(true)

const fetchFeaturedProducts = async () => {
  try {
    const res = await fetch('/api/products')
    const data = await res.json()
    if (data.success) {
      featuredProducts.value = data.data.filter(p => !p.is_out_of_stock).slice(0, 4).map(p => ({
        id: p.id,
        slug: p.slug,
        title: p.name,
        image: p.image_url || '/images/placeholder.png',
        price: p.price,
        original_price: p.original_price,
        category: p.category
      }))
    }
  } catch (e) {
    // API might be down too - that's okay
  } finally {
    loading.value = false
  }
}

const quickLinks = [
  { name: 'หน้าหลัก', to: '/', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', desc: 'กลับไปยังหน้าแรก' },
  { name: 'แคตตาล็อกสินค้า', to: '/products', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', desc: 'ดูสินค้ากว่า 100 แบบ' },
  { name: 'ขอใบเสนอราคา', to: '/quotation', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', desc: 'รับราคาพิเศษ' },
  { name: 'ติดต่อเรา', to: '/contact', icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', desc: 'แจ้งปัญหากับทีมงาน' },
]

onMounted(() => {
  fetchFeaturedProducts()
})
</script>

<template>
  <div class="min-h-screen bg-[#f8f9fa] dark:bg-[#0a0f16] pt-24 md:pt-32 pb-10 md:pb-24 transition-colors">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      <!-- Hero Error Section -->
      <div class="text-center max-w-3xl mx-auto mb-16 md:mb-20">
        <!-- Animated 500 Badge -->
        <div class="relative w-36 h-36 mx-auto mb-8">
          <div class="absolute inset-0 bg-red-500/15 dark:bg-red-500/10 blur-3xl rounded-full animate-pulse-slow"></div>
          <div class="relative z-10 w-full h-full bg-white dark:bg-[#111827] border border-red-100 dark:border-red-900/30 rounded-[2rem] flex flex-col items-center justify-center shadow-2xl shadow-red-100/50 dark:shadow-none animate-float">
            <svg class="w-10 h-10 text-red-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
            <span class="text-3xl font-black text-red-400">500</span>
          </div>
          <div class="absolute -top-3 -right-3 w-6 h-6 bg-red-400 rounded-full opacity-60 animate-ping-slow"></div>
        </div>

        <h1 class="text-3xl md:text-5xl font-black text-gray-900 dark:text-white tracking-tight mb-4">
          เกิดข้อผิดพลาดภายในระบบ
        </h1>
        <p class="text-gray-500 dark:text-gray-400 font-light text-lg md:text-xl mb-8 max-w-xl mx-auto">
          ขออภัยเป็นอย่างยิ่ง ระบบมีปัญหาชั่วคราว ทีมงานได้รับการแจ้งเตือนเรียบร้อยแล้ว ระหว่างนี้คุณสามารถดูสินค้าและบริการของเราได้ด้านล่าง
        </p>

        <!-- Quick Action Buttons -->
        <div class="flex flex-wrap items-center justify-center gap-3">
          <button @click="$router.go(0)" class="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold rounded-full transition-all hover:bg-emerald-600 dark:hover:bg-emerald-500 dark:hover:text-white hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] transform hover:-translate-y-0.5">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
            ลองใหม่อีกครั้ง
          </button>
          <router-link to="/" class="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white dark:bg-[#111827] text-gray-900 dark:text-white font-bold rounded-full border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 transition-all hover:shadow-lg transform hover:-translate-y-0.5">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
            กลับหน้าแรก
          </router-link>
        </div>
      </div>

      <!-- Quick Navigation Cards -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16 md:mb-20">
        <router-link 
          v-for="link in quickLinks" 
          :key="link.to" 
          :to="link.to"
          class="group bg-white dark:bg-[#111827] border border-gray-100 dark:border-gray-800 rounded-2xl p-5 hover:border-emerald-300 dark:hover:border-emerald-600 hover:shadow-lg hover:-translate-y-0.5 transition-all"
        >
          <div class="w-12 h-12 mb-4 rounded-xl bg-gray-50 dark:bg-gray-800 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-900/20 flex items-center justify-center transition-colors">
            <svg class="w-6 h-6 text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="link.icon"></path></svg>
          </div>
          <h3 class="font-bold text-gray-900 dark:text-white text-sm mb-1">{{ link.name }}</h3>
          <p class="text-xs text-gray-500 dark:text-gray-400">{{ link.desc }}</p>
        </router-link>
      </div>

      <!-- Featured Products (may not load if API is down) -->
      <div v-if="featuredProducts.length > 0" class="mb-16 md:mb-20">
        <div class="flex items-center justify-between mb-8">
          <div>
            <p class="text-xs font-bold tracking-[0.2em] text-emerald-600 dark:text-emerald-400 uppercase mb-2">ระหว่างรอ</p>
            <h2 class="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">ดูสินค้าที่น่าสนใจ</h2>
          </div>
          <router-link to="/products" class="hidden sm:inline-flex text-sm font-bold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 items-center gap-1.5 transition-colors">
            ดูทั้งหมด
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
          </router-link>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <ProductCard 
            v-for="product in featuredProducts" 
            :key="product.id"
            :product="product"
          />
        </div>
      </div>

      <!-- Contact CTA -->
      <div class="bg-white dark:bg-[#111827] border border-gray-100 dark:border-gray-800 rounded-3xl p-8 md:p-12 text-center shadow-sm">
        <div class="w-14 h-14 mx-auto mb-5 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
          <svg class="w-7 h-7 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
        </div>
        <h3 class="text-xl font-black text-gray-900 dark:text-white mb-2">ยังพบปัญหาอยู่?</h3>
        <p class="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">หากปัญหาเกิดขึ้นซ้ำ กรุณาแจ้งทีมงานของเรา เราพร้อมช่วยเหลือคุณ</p>
        <router-link to="/contact" class="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-full transition-all hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] transform hover:-translate-y-0.5">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
          ติดต่อทีมงาน
        </router-link>
      </div>

      <p class="text-xs text-gray-400 dark:text-gray-600 mt-8 text-center">Error Code: 500 — Internal Server Error</p>
    </div>
  </div>
</template>

<style scoped>
.animate-float {
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
}

.animate-pulse-slow {
  animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.animate-ping-slow {
  animation: ping 3s cubic-bezier(0, 0, 0.2, 1) infinite;
}
</style>
